// Libs
import classNames from 'classnames/bind';
import * as yup from 'yup';
import { useEffect } from 'react';
import { TFunction } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material';
// Components, Layouts, Pages
import { BaseInputSelect } from '~/components/common';
// Others
import { IFormPriceBookName } from '~/utils/interface/priceBook';
import {
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './PriceBookModal.module.scss';

type Props = {
  businessPriceBook?: string;
  onClose: () => void;
  handleSubmitForm: (data: IFormPriceBookName) => void;
};

const cx = classNames.bind(styles);

export const schema = (t: TFunction) => {
  return yup
    .object()
    .shape({
      businessPriceBook: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
    })
    .required();
};

export const defaultValuesForm: IFormPriceBookName = {
  businessPriceBook: EMPTY_STRING,
  businessPriceBookType: EMPTY_STRING,
};

const PriceBookModal = (props: Props) => {
  //#region Destructuring Props
  const { businessPriceBook, onClose, handleSubmitForm } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IFormPriceBookName>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!businessPriceBook) return;

    reset({ businessPriceBook });
  }, [businessPriceBook, reset]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitFormPriceBook = (data: IFormPriceBookName) => {
    handleSubmitForm(data);
    onClose();
  };

  const handleCancelModal = () => {
    reset(defaultValuesForm);
    onClose();
  };

  const handleSelectChange = (value: string[], type?: string) => {
    setValue('businessPriceBook', value[DEFAULT_NUMBER_ZERO] || EMPTY_STRING);
    setValue('businessPriceBookType', type);
  };
  //#endregion Handle Function

  return (
    <Dialog id='priceBookModalComponent' open={true} fullWidth maxWidth={'sm'} onClose={handleCancelModal}>
      <DialogContent dividers={true}>
        <Box component={'form'} className={cx('form')}>
          <Controller
            name='businessPriceBook'
            control={control}
            render={({ field: { value } }) => (
              <BaseInputSelect
                maxItem={1}
                label={t('common_modal_price_book_name')}
                value={value ? [value] : []}
                options={[]}
                errorMessage={errors?.businessPriceBook?.message}
                onChange={handleSelectChange}
                required
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={handleCancelModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' onClick={handleSubmit(handleSubmitFormPriceBook)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default PriceBookModal;
